// Agent request-configuration controls for the Dev Console composer:
// conversation mode, model, per-model parameters (effort levels etc.), and
// advanced cloud-agent options.
//
// Source of truth is the Cursor SDK (@cursor/sdk v1.0.27):
// - Modes: AgentModeOption = 'agent' | 'plan' (options.d.ts). There is no
//   runtime discovery endpoint for modes; /dev/status serves the list and
//   FALLBACK_MODES covers older backends. "Auto" is NOT a mode — it's a
//   model selection (`auto`, or `auto-smart` + `optimize_for` when Cursor
//   Router is enabled) and shows up in the model dropdown via the catalog.
// - Models + effort: the /v1/models catalog (Cursor.models.list()) returns
//   ModelListItem { id, displayName, description, parameters, variants }.
//   "Effort" (reasoning effort / thinking level, fast toggle, Router's
//   optimize_for) arrives as ModelParameterDefinition entries whose ids and
//   values vary per model — these components render whatever the catalog
//   declares instead of hardcoding a list, so new params need no UI change.
//
// Shapes (mirroring the SDK types, since this codebase is plain JS):
//   ModelDef:   { id, name, description?, parameters?: ParamDef[], variants?: Variant[] }
//   ParamDef:   { id, displayName, values: [{ value, displayName }] }
//   Variant:    { displayName, isDefault, params: [{ id, value }] }
//   picks:      { [paramId]: value }  — the UI's param selection state
import React from 'react';

export const FALLBACK_MODES = ['auto', 'agent', 'plan'];

// ---- last-used config persistence ------------------------------------------
// The composer remembers the last mode/model/params picked for a NEW session
// (localStorage, per browser). Restores are validated against the LIVE
// catalog — a stored model that is no longer available is dropped (back to
// the account default), and stored param values that the catalog no longer
// allows fall back to that model's defaults.
const STORAGE_KEY = 'devc-agent-config';

export function loadStoredConfig() {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

export function saveStoredConfig(patch) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...loadStoredConfig(), ...patch }));
  } catch { /* storage unavailable — persistence is best-effort */ }
}

// Re-validate a stored model pick against the current catalog. Returns
// `{ id, picks }` when the model still exists (picks cleaned per-parameter),
// or null when it vanished and the caller should fall back to the default.
export function restoreModelPick(models, stored) {
  const def = (models || []).find((m) => m.id === stored?.model);
  if (!def) return null;
  const defaults = defaultParamsFor(def);
  const storedParams = stored.params && typeof stored.params === 'object' ? stored.params : {};
  if ((def.parameters || []).length) {
    const picks = { ...defaults };
    Object.entries(storedParams).forEach(([id, value]) => {
      const p = def.parameters.find((x) => x.id === id);
      if (p?.values?.some((v) => v.value === value)) picks[id] = value;
    });
    return { id: def.id, picks };
  }
  // Variant-only model: accept the stored picks only if they still match one
  // of the catalog's presets exactly.
  const match = (def.variants || []).find((v) => {
    const params = v.params || [];
    return params.length === Object.keys(storedParams).length
      && params.every((p) => storedParams[p.id] === p.value);
  });
  return { id: def.id, picks: match ? { ...storedParams } : defaults };
}

const MODE_LABEL = { auto: 'Auto', agent: 'Agent', plan: 'Plan' };
const MODE_HINT = {
  auto: 'The console decides: feature-sized or vague asks get a plan to approve first, small concrete fixes go straight to changes',
  agent: 'Implement changes directly',
  plan: 'Explore and produce a plan before building',
};

// Default parameter picks for a model: the catalog's default variant when one
// is flagged, else each parameter's first allowed value — which is exactly
// what the run would use if params were omitted (per SDK docs), surfaced in
// the UI instead of hidden. Variant-only models (variants but no parameter
// definitions) default to the default variant's params too.
export function defaultParamsFor(modelDef) {
  if (!modelDef) return {};
  const picks = {};
  (modelDef.parameters || []).forEach((p) => {
    if (p.values?.length) picks[p.id] = p.values[0].value;
  });
  const def = (modelDef.variants || []).find((v) => v.isDefault);
  (def?.params || []).forEach((p) => { picks[p.id] = p.value; });
  return picks;
}

// Turn the picks record into the ordered params array the API expects.
// Ordered by the model's parameter definitions; for variant-only models it
// falls back to whatever the picked variant put into the record.
export function buildParamsArray(modelDef, picks) {
  const defs = modelDef?.parameters || [];
  if (defs.length) {
    return defs
      .filter((p) => picks[p.id] !== undefined)
      .map((p) => ({ id: p.id, value: picks[p.id] }));
  }
  return Object.entries(picks || {}).map(([id, value]) => ({ id, value }));
}

// Segmented control for the conversation mode.
export function ModeSelector({ modes, value, onChange }) {
  const list = modes?.length ? modes : FALLBACK_MODES;
  return (
    <div className="devc-mode" role="radiogroup" aria-label="Conversation mode">
      {list.map((m) => (
        <button
          key={m}
          type="button"
          className={value === m ? 'active' : ''}
          title={MODE_HINT[m] || m}
          onClick={() => onChange(m)}
        >
          {MODE_LABEL[m] || m}
        </button>
      ))}
    </div>
  );
}

// Conversation profile (backend shared/devModes.js): Balanced / Economy /
// Quality / Design / … — each a routing policy (tier band, plan bias, cue).
// The list is what the seat may pick; the backend re-checks on create.
export function RoutingModeSelector({ modes, value, onChange, disabled = false }) {
  if (!modes?.length) return null;
  const cur = modes.find((m) => m.id === value) || null;
  return (
    <label className="devc-param" title={cur ? `${cur.description} · tiers ${cur.tierMin}–${cur.tierMax}${cur.conversation !== 'auto' ? ` · always ${cur.conversation}` : ''}` : 'How this conversation routes: the model tier band and whether it plans first'}>
      <span>Profile</span>
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled} aria-label="Conversation profile">
        {modes.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
      </select>
    </label>
  );
}

// Repo dropdown — which repo a NEW session works (dev.repos, /dev/status
// `repos`). Hidden when the console has only its own app. Kinds: app /
// layer (this app), kit / plane (granted to the foundry by the control
// plane — never addable here).
export function RepoSelector({ repos, value, onChange, disabled = false }) {
  if (!repos || repos.length < 2) return null;
  const cur = repos.find((r) => r.id === value) || repos[0];
  return (
    <label className="devc-param" title={cur ? `${cur.slug}${cur.kind === 'kit' ? ' — the App Kit (ships via kit-dev + a kit release)' : cur.kind === 'plane' ? ' — the control plane (ships nventr.io itself)' : ''}` : 'Repository this conversation works in'}>
      <span>Repo</span>
      <select value={value || 'default'} onChange={(e) => onChange(e.target.value)} disabled={disabled} aria-label="Repository">
        {repos.map((r) => <option key={r.id} value={r.id}>{r.label}{r.kind === 'kit' || r.kind === 'plane' ? ` (${r.kind})` : ''}</option>)}
      </select>
    </label>
  );
}

// Model dropdown. `emptyLabel` names what an empty pick means: the account
// default on new sessions, "keep current" on follow-ups.
export function ModelSelector({ models, value, onChange, emptyLabel, excludeIds = [] }) {
  if (!models?.length) return null;
  return (
    <select
      className="devc-model"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      title="Model for this session"
    >
      <option value="">{emptyLabel}</option>
      {models.filter((m) => !excludeIds.includes(m.id)).map((m) => (
        <option key={m.id} value={m.id} title={m.description || undefined}>{m.name}</option>
      ))}
    </select>
  );
}

// Per-model parameter dropdowns (effort/reasoning level, fast toggle,
// optimize_for, …) — one select per catalog parameter definition. A parameter
// with a single allowed value renders disabled (visible, not dropped).
// Variant-only models render one preset dropdown instead.
export function ModelParamsSelector({ modelDef, picks, onChange }) {
  if (!modelDef) return null;
  const defs = modelDef.parameters || [];
  if (defs.length) {
    return defs.map((p) => (
      <label key={p.id} className="devc-param" title={`${modelDef.name} · ${p.displayName || p.id}`}>
        <span>{p.displayName || p.id}</span>
        <select
          value={picks[p.id] ?? ''}
          disabled={(p.values || []).length < 2}
          onChange={(e) => onChange({ ...picks, [p.id]: e.target.value })}
        >
          {(p.values || []).map((v) => (
            <option key={v.value} value={v.value}>{v.displayName || v.value}</option>
          ))}
        </select>
      </label>
    ));
  }
  const variants = modelDef.variants || [];
  if (!variants.length) return null;
  const matches = (v) => (v.params || []).every((p) => picks[p.id] === p.value)
    && (v.params || []).length === Object.keys(picks || {}).length;
  const current = variants.findIndex(matches);
  return (
    <label className="devc-param" title={`${modelDef.name} preset`}>
      <span>Preset</span>
      <select
        value={current >= 0 ? current : 0}
        disabled={variants.length < 2}
        onChange={(e) => {
          const v = variants[Number(e.target.value)];
          const next = {};
          (v?.params || []).forEach((p) => { next[p.id] = p.value; });
          onChange(next);
        }}
      >
        {variants.map((v, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <option key={i} value={i}>{v.displayName}{v.isDefault ? ' (default)' : ''}</option>
        ))}
      </select>
    </label>
  );
}

export const DEFAULT_AGENT_OPTIONS = {
  autoCreatePR: true,
  skipReviewerRequest: true,
  workOnCurrentBranch: false,
  openAsCursorGithubApp: '', // '' = SDK default | 'true' | 'false'
  startingRef: '',
};

// Convert the UI state into the wire shape for POST /dev/sessions.
export function agentOptionsPayload(o) {
  return {
    autoCreatePR: o.autoCreatePR,
    skipReviewerRequest: o.skipReviewerRequest,
    ...(o.workOnCurrentBranch ? { workOnCurrentBranch: true } : {}),
    ...(o.openAsCursorGithubApp !== '' ? { openAsCursorGithubApp: o.openAsCursorGithubApp === 'true' } : {}),
    ...(o.startingRef.trim() ? { startingRef: o.startingRef.trim() } : {}),
  };
}

// Collapsible advanced cloud-agent options (SDK CloudAgentOptions subset that
// is safe to drive per-session from the browser — the rest is server-owned;
// see the audit note in backend/api/devAgent.js cleanAgentOptions).
export function AdvancedAgentOptions({ value, onChange }) {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <details className="devc-advanced">
      <summary title="How the agent hands its work back: PR, reviewer, branch, starting point">Agent options</summary>
      <div className="devc-advanced-body">
        <p className="devc-dim" style={{ margin: '0 0 6px', fontSize: 12 }}>
          How the agent hands work back. Defaults suit most runs — hover any option for what it does.
        </p>
        <label title="Open a GitHub PR automatically when the run finishes (Merge & Deploy needs one)">
          <input type="checkbox" checked={value.autoCreatePR} onChange={(e) => set({ autoCreatePR: e.target.checked })} />
          Auto-create PR
        </label>
        <label title="Don't request a human reviewer on the PR">
          <input type="checkbox" checked={value.skipReviewerRequest} onChange={(e) => set({ skipReviewerRequest: e.target.checked })} />
          Skip reviewer request
        </label>
        <label title="Commit to the starting branch instead of a new agent branch">
          <input type="checkbox" checked={value.workOnCurrentBranch} onChange={(e) => set({ workOnCurrentBranch: e.target.checked })} />
          Work on current branch
        </label>
        <label title="Author the PR as the Cursor GitHub App instead of the API-key owner (default depends on key type)">
          PR author
          <select value={value.openAsCursorGithubApp} onChange={(e) => set({ openAsCursorGithubApp: e.target.value })}>
            <option value="">Key default</option>
            <option value="true">Cursor GitHub App</option>
            <option value="false">API-key owner</option>
          </select>
        </label>
        <label title="Branch, tag, or SHA to start the agent from (default: repo default branch)">
          Starting ref
          <input
            type="text"
            value={value.startingRef}
            placeholder="default branch"
            onChange={(e) => set({ startingRef: e.target.value })}
          />
        </label>
      </div>
    </details>
  );
}
